/**
 * `/sites` — the mine-site locator. The demo's Australia map (`mapSVG`, line
 * 2034) beside its site heat table (`siteHeat`, line 3151).
 *
 * Six pins, coloured by the worst machine band at the site and labelled with
 * the machine count; clicking one opens that site's page. The table repeats
 * the same six rows with the full band distribution, the open alerts and the
 * closed incidents, so the page is readable without the map — which matters
 * for a screen reader and for a narrow viewport.
 *
 * Below the map, the demo's overview (`renderOverview`, line 2111) adds six
 * site cards — spec, workings, one icon per machine, and the week's
 * availability — and eight summary measures across the whole fleet. Both are
 * derived in the transform.
 *
 * The map, the heat table and the site cards paint from eager data. The
 * availability figures, the drafted orders, emissions and the data-store counts
 * need lazy domains; those cells show a skeleton until the fetch lands, and an
 * inline error if it fails — the rest of the page is unaffected either way.
 *
 * ## Zoom, pan and label dragging
 *
 * The map is `MineMap` in its `interactive` mode: wheel, double-click and
 * button zoom, drag to pan, and draggable site names, each with a reset. All of
 * it is component state — leaving the page puts the map back at rest.
 *
 * @see src/versions/v1/modules/sites/SitesMapScene.transform.ts
 * @see docs/logic/sites/sites-map.md
 */

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HelpAnchor,
  InlineError,
  SkeletonBlock,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { unpackTable } from '../../shared/columnar';
import { useSourceDrawer } from '../issues/useSourceDrawer';
import { routeForView } from '../../shared/oem-routes';
import { useOemData, type ShiftDailyRow, type WorkOrder } from '../../shared/useOemData';
import { MineMap } from '../../shared/viz/MineMap';
import { DRIVE_COLOUR, SiteMachineIcon } from './SiteMachineIcon';
import {
  dataStoreMeasure,
  DRIVE_LABEL,
  eagerMeasures,
  siteCardAvailability,
  siteCards,
  sitesOverview,
  weekMeasures,
  STORE_SOURCE,
  type MeasureSource,
  type DriveKey,
} from './SitesMapScene.transform';

/** The demo's standfirst, cut to one sentence (simplification pass B4). */
const STANDFIRST =
  'Six mine sites across four states, each pin carrying the band of the worst machine on it.';

export function SitesMapScene() {
  const navigate = useNavigate();
  const rows = useMemo(() => sitesOverview(), []);
  const pins = useMemo(() => rows.map((r) => r.pin), [rows]);
  const cards = useMemo(() => siteCards(), []);
  const eager = useMemo(() => eagerMeasures(), []);

  const telemetry = useOemData('telemetry');
  const workOrders = useOemData('work-orders');
  const docIndex = useOemData('doc-index');
  const parts = useOemData('parts');
  const comms = useOemData('comms');

  const ctx = useMemo(
    () =>
      telemetry.data && workOrders.data
        ? {
            shiftDaily: unpackTable<ShiftDailyRow>(telemetry.data.shift_daily),
            workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
          }
        : null,
    [telemetry.data, workOrders.data],
  );
  const week = useMemo(() => (ctx ? weekMeasures(ctx) : null), [ctx]);

  // Demo `FIG(...)` on the measures: the figure opens its rows, with the calc box.
  const { openDataset } = useSourceDrawer();
  const openSource = (src: MeasureSource) =>
    openDataset(src.dataset, { title: src.title, filter: src.filter, calc: src.calc });
  const siteAvail = useMemo(() => (ctx ? siteCardAvailability(ctx) : null), [ctx]);
  const store = useMemo(
    () =>
      telemetry.data && workOrders.data && docIndex.data && parts.data && comms.data
        ? dataStoreMeasure({
            telemetry: telemetry.data,
            workOrders: workOrders.data,
            docIndex: docIndex.data,
            parts: parts.data,
            comms: comms.data,
          })
        : null,
    [telemetry.data, workOrders.data, docIndex.data, parts.data, comms.data],
  );
  const weekError = telemetry.error ?? workOrders.error;
  const storeError = weekError ?? docIndex.error ?? parts.error ?? comms.error;

  return (
    <SurfacePageLayout width="full">
      <Commentable name="SitesMapScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-8" as="div">
            <SurfacePageHeader
              title="Where the fleet is"
              description={STANDFIRST}
              actions={<HelpAnchor doc="sites/sites-map" />}
            />


            {/* Container query, not a viewport query: the scene column halves in
                width when the AI rail is open, so the split must follow the
                column rather than the window. */}
            <div className="grid grid-cols-1 gap-8 @4xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
              <Commentable name="SitesMap">
                <section className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
                  <MineMap
                    interactive
                    pins={pins}
                    onSelect={(mine) => navigate(`/sites/${mine}`)}
                    className="mx-auto w-full max-w-[380px]"
                  />
                  <p className="mt-2 text-[11px] text-muted">
                    Pin colour is the band of the worst machine on site. Select a pin to open the
                    site page.
                  </p>
                </section>
              </Commentable>

              <Commentable name="SiteHeatTable">
                <section className="space-y-2">
                  <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                    Condition bands and what is open
                  </h2>
                  <Table clickableRows>
                    <THead>
                      <TR>
                        <TH>Site</TH>
                        <TH className="text-right">Machines</TH>
                        <TH>Condition distribution</TH>
                        <TH className="text-right">Alerts</TH>
                        <TH className="text-right">Incidents</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {rows.map((row) => (
                        <TR key={row.mine} onRowClick={() => navigate(`/sites/${row.mine}`)}>
                          <TD>
                            <span className="font-medium text-zinc-900 dark:text-zinc-100">
                              {row.name}
                            </span>
                            <span className="ml-2 text-xs text-muted">{row.state}</span>
                          </TD>
                          <TD className="mono text-right">{row.machines}</TD>
                          <TD className="min-w-[12rem]">
                            <span
                              className="band-bar"
                              role="img"
                              aria-label={row.bands
                                .filter((b) => b.n > 0)
                                .map((b) => `${b.n} ${b.band.label.toLowerCase()}`)
                                .join(', ')}
                            >
                              {row.bands
                                .filter((b) => b.n > 0)
                                .map((b) => (
                                  <i
                                    key={b.band.key}
                                    style={{ flex: b.n, background: `var(--band-${b.band.key})` }}
                                    title={`${b.n} ${b.band.label.toLowerCase()}`}
                                  />
                                ))}
                            </span>
                          </TD>
                          <TD className="text-right">
                            {row.alerts ? (
                              <span className="band-pill band-vpoor">{row.alerts}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </TD>
                          <TD className="text-right">
                            {row.incidents ? (
                              <span className="mono">{row.incidents}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </section>
              </Commentable>
            </div>

            <Commentable name="SiteCards">
              <section className="space-y-2" aria-labelledby="site-cards-heading">
                <h2
                  id="site-cards-heading"
                  className="text-lg font-semibold text-zinc-900 dark:text-zinc-100"
                >
                  The six sites
                </h2>
                <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
                  {cards.map((card) => (
                    <article
                      key={card.mine}
                      className="flex min-w-0 flex-col gap-2 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg transition-colors hover:bg-[var(--glass-surface-strong)]"
                    >
                      <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                        <Link
                          to={card.href}
                          className="hover:underline hover:underline-offset-2"
                        >
                          {card.name}
                        </Link>
                        <span className="ml-auto rounded bg-zinc-100 px-1.5 py-px text-[10px] font-medium tracking-wider text-muted dark:bg-zinc-800">
                          {card.state}
                        </span>
                      </h3>
                      <p className="flex flex-wrap items-center gap-2 text-[11px] text-muted">
                        <span className={`band-pill ${card.isLive ? 'band-good' : 'band-fair'}`}>
                          {card.dataMode}
                        </span>
                        {card.region}
                      </p>
                      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                        {card.spec.map(([label, value]) => (
                          <div key={label} className="contents">
                            <dt className="whitespace-nowrap text-muted">{label}</dt>
                            <dd className="min-w-0 text-zinc-900 dark:text-zinc-100">
                              {value}
                              {label === 'Ownership' && card.contractor ? (
                                <span className="text-muted"> ({card.contractor})</span>
                              ) : null}
                            </dd>
                          </div>
                        ))}
                      </dl>
                      {card.workings.length ? (
                        <ul className="flex flex-wrap gap-1" aria-label="Machines by working">
                          {card.workings.map((w) => (
                            <li
                              key={w.label}
                              className="max-w-full truncate rounded-full bg-zinc-100 px-2 py-px text-[10px] text-muted dark:bg-zinc-800"
                            >
                              {w.label}{' '}
                              <b className="text-zinc-900 dark:text-zinc-100">{w.machines}</b>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <div
                        className="grid grid-cols-[repeat(auto-fill,minmax(36px,1fr))] gap-1"
                        role="group"
                        aria-label={`Machines at ${card.name}`}
                      >
                        {card.machines.map((machine) => (
                          <SiteMachineIcon key={machine.id} machine={machine} />
                        ))}
                      </div>
                      <div className="mt-auto flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted">
                        <span>{card.machines.length} machines</span>
                        <span style={{ color: 'var(--band-vpoor)' }}>
                          {card.veryPoorOrWorse} very poor or worse
                        </span>
                        <span style={{ color: 'var(--band-poor)' }}>{card.poor} poor</span>
                        <span>
                          availability{' '}
                          {siteAvail ? (
                            <span className="mono">
                              {siteAvail[card.mine] != null ? `${siteAvail[card.mine]}%` : '—'}
                            </span>
                          ) : weekError ? (
                            '—'
                          ) : (
                            <SkeletonBlock className="inline-block h-3 w-8 align-middle" />
                          )}
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
                <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted">
                  {(Object.keys(DRIVE_LABEL) as DriveKey[]).map((drive) => (
                    <span key={drive} className="inline-flex items-center gap-1.5">
                      <i
                        aria-hidden="true"
                        className="inline-block h-2 w-2 rounded-sm"
                        style={{ background: DRIVE_COLOUR[drive] }}
                      />
                      {DRIVE_LABEL[drive]}
                    </span>
                  ))}
                  <span>corner dot = condition band</span>
                  <span className="ml-auto">
                    {eager.incidents} incidents this fortnight ·{' '}
                    <Link
                      to={routeForView('incident') ?? '/issues/incidents'}
                      className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                    >
                      open
                    </Link>
                  </span>
                </p>
              </section>
            </Commentable>

            <Commentable name="SiteSummaryMeasures">
              <section aria-labelledby="site-measures-heading" className="space-y-2">
                <h2
                  id="site-measures-heading"
                  className="text-lg font-semibold text-zinc-900 dark:text-zinc-100"
                >
                  What is being monitored
                </h2>
                {weekError ? (
                  <InlineError>{`Weekly figures did not load: ${weekError.message}`}</InlineError>
                ) : null}
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-4">
                  <Measure
                    label="Machines monitored"
                    value={String(eager.machines)}
                    unit={`across ${eager.mines} mines`}
                    detail={eager.fleetMix}
                  />
                  <Measure
                    label="Machine condition · by band"
                    value={
                      <span className="flex flex-wrap items-baseline gap-x-2 text-xl">
                        {eager.bands.map(({ band, n }) => (
                          <span key={band.key} className="whitespace-nowrap">
                            <span style={{ color: band.colorToken }}>{n}</span>
                            <span className="ml-0.5 text-xs font-normal text-muted">
                              {band.key === 'major' ? 'major' : band.label.toLowerCase()}
                            </span>
                          </span>
                        ))}
                      </span>
                    }
                    detail="Band = lowest component condition score on the machine (0–100)"
                  >
                    <span
                      className="band-bar"
                      role="img"
                      aria-label={eager.bands.map((b) => `${b.n} ${b.band.label.toLowerCase()}`).join(', ')}
                    >
                      {eager.bands
                        .filter((b) => b.n > 0)
                        .map((b) => (
                          <i
                            key={b.band.key}
                            style={{ flex: b.n, background: `var(--band-${b.band.key})` }}
                          />
                        ))}
                    </span>
                  </Measure>
                  <Measure
                    label={
                      <>
                        Fleet availability · week to 06 Sep
                        {week ? (
                          <>
                            {' · '}
                            <span style={{ color: week.availabilityBand.colorToken }}>
                              {week.availabilityBand.label}
                            </span>
                          </>
                        ) : null}
                      </>
                    }
                    value={week ? String(week.availability) : null}
                    failed={Boolean(weekError)}
                    source={week?.sources.availability}
                    onOpen={openSource}
                    unit="% of scheduled hours"
                    detail={week?.availabilityNote ?? null}
                  />
                  <Measure
                    label="Awaiting a human decision"
                    value={week ? String(week.drafts) : null}
                    failed={Boolean(weekError)}
                    source={week?.sources.drafts}
                    onOpen={openSource}
                    unit="agent-drafted work orders"
                    valueColour="var(--warn)"
                    detail={week?.draftList ?? null}
                  />
                  <Measure
                    label="Connected systems"
                    value={String(eager.connectors)}
                    unit="connectors"
                    detail={eager.connectorsDetail}
                  />
                  <Measure
                    label="Data in the store"
                    value={store ? store.workOrders.toLocaleString('en-AU') : null}
                    failed={Boolean(storeError)}
                    source={store ? STORE_SOURCE : undefined}
                    onOpen={openSource}
                    unit="work orders"
                    detail={store?.detail ?? null}
                  />
                  <Measure
                    label="Open alerts"
                    value={String(eager.alerts.length)}
                    valueColour="var(--band-major)"
                    detail={
                      <span className="flex flex-wrap gap-x-2 gap-y-0.5">
                        {eager.alerts.map((a) => (
                          <Link
                            key={a.id}
                            to={a.href}
                            className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                          >
                            {a.label}
                          </Link>
                        ))}
                      </span>
                    }
                  />
                  <Measure
                    label="Emissions · week to 06 Sep"
                    value={week ? String(week.emissionsT) : null}
                    failed={Boolean(weekError)}
                    source={week?.sources.emissions}
                    onOpen={openSource}
                    unit="t CO₂-e"
                    detail="Diesel scope 1 + grid electricity scope 2 · site factors in the cost summary"
                  />
                </div>
              </section>
            </Commentable>

            {/* B3: the ask chips sit under the map, not above it. */}
            <AskStrip view="overview" />
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

/**
 * Demo `.card.kpi` (line 2123). One summary measure: eyebrow, figure with its
 * unit, optional graphic, detail line. `value === null` means the figure is
 * still loading — a skeleton holds its place so the grid does not jump — or,
 * with `failed`, that its fetch failed and the figure reads as a dash.
 */
function Measure({
  label,
  value,
  unit,
  detail,
  valueColour,
  failed = false,
  source,
  onOpen,
  children,
}: {
  label: React.ReactNode;
  value: React.ReactNode | null;
  unit?: string;
  detail: React.ReactNode | null;
  valueColour?: string;
  failed?: boolean;
  /** The rows behind the figure; with `onOpen`, the figure becomes a drawer link. */
  source?: MeasureSource;
  onOpen?: (source: MeasureSource) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
      <p className="text-[11px] uppercase tracking-wider text-muted">{label}</p>
      <div
        className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold text-zinc-900 dark:text-zinc-100"
        style={valueColour && value !== null ? { color: valueColour } : undefined}
      >
        {value !== null && source && onOpen ? (
          <button
            type="button"
            onClick={() => onOpen(source)}
            title="Open the rows behind this figure"
            className="rounded underline decoration-dotted decoration-1 underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          >
            {value}
          </button>
        ) : value !== null ? (
          value
        ) : failed ? (
          '—'
        ) : (
          <SkeletonBlock className="h-7 w-16" />
        )}
        {unit && value !== null ? (
          <span className="text-xs font-normal text-muted">{unit}</span>
        ) : null}
      </div>
      {children}
      <div className="mt-1 text-[11px] text-muted">
        {detail !== null ? detail : failed ? null : <SkeletonBlock className="h-3 w-full" />}
      </div>
    </div>
  );
}
